import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Heart, Users, Briefcase, Home, ArrowRight, Settings, Lock, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import ReviewerBadge from "@/components/ReviewerBadge";
import AdminPanel from "@/components/AdminPanel";
import UpgradePrompt from "@/components/UpgradePrompt";
import { useAccess } from "@/contexts/AccessContext";

const relationshipTypes = [
  {
    id: "general",
    label: "General",
    description: "Anyone — friend, family, or other",
    icon: MessageCircle,
  },
  {
    id: "romantic",
    label: "Romantic Partner",
    description: "Spouse, boyfriend, girlfriend, dating",
    icon: Heart,
  },
  {
    id: "family",
    label: "Family Member",
    description: "Parent, sibling, child, relative",
    icon: Home,
  },
  {
    id: "friend",
    label: "Friend",
    description: "Close friend, acquaintance, roommate",
    icon: Users,
  },
  {
    id: "work",
    label: "Work Colleague",
    description: "Boss, coworker, client, employee",
    icon: Briefcase,
  }
];

export default function LandingPage({ setRelationshipType }) {
  const navigate = useNavigate();
  const { isReviewer, isLimitedMode, hasFullAccess, remainingUses, freeRelationships } = useAccess();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);

  const handleSelectRelationship = (type) => {
    // Check if relationship is locked in limited mode
    const isLocked = isLimitedMode && !hasFullAccess && !freeRelationships.includes(type.id);
    
    if (isLocked) {
      setShowUpgradePrompt(true);
      return;
    }
    
    setRelationshipType(type);
    navigate("/analyze");
  };

  // In limited mode without full access, show General first, then locked ones
  const allUnlocked = !isLimitedMode || hasFullAccess;

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 bg-[#F7F9FB] relative">
      {/* Admin Panel Modal */}
      {showAdminPanel && (
        <AdminPanel onClose={() => setShowAdminPanel(false)} />
      )}
      
      {/* Upgrade Prompt Modal */}
      {showUpgradePrompt && (
        <UpgradePrompt onClose={() => setShowUpgradePrompt(false)} />
      )}

      {/* Admin button - top right */}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setShowAdminPanel(true)}
        data-testid="admin-button"
        className="absolute top-4 right-4 text-[#6B7280] hover:text-[#1F3A5F] hover:bg-[#EAF6F4] rounded-xl"
      >
        <Settings size={18} />
      </Button>

      {/* Logo */}
      <div className="mb-6 animate-fadeIn">
        <Logo size="large" />
      </div>
      
      {/* Reviewer Badge */}
      {isReviewer && (
        <div className="mb-4 animate-fadeIn">
          <ReviewerBadge />
        </div>
      )}

      {/* Hero Section */}
      <div className="text-center mb-10 max-w-xl animate-fadeIn" style={{ animationDelay: '0.1s' }}>
        <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1F3A5F] mb-5 leading-tight">
          Stop sending messages you'll regret
        </h1>
        <p className="text-lg text-[#6B7280] leading-relaxed mb-4">
          Strong emotions can lead to words we don't mean.
        </p>
        <p className="text-base text-[#6B7280] leading-relaxed">
          SayItBetter helps you respond thoughtfully instead.
        </p>
      </div>

      {/* Relationship Type Selection */}
      <div className="w-full max-w-2xl">
        <p className="text-center text-[#1F3A5F] font-medium mb-6 animate-fadeIn stagger-2" data-testid="select-prompt">
          Who are you messaging?
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="relationship-grid">
          {relationshipTypes.map((type, index) => {
            const Icon = type.icon;
            const isLocked = !allUnlocked && !freeRelationships.includes(type.id);
            
            return (
              <div
                key={type.id}
                data-testid={`relationship-${type.id}`}
                data-locked={isLocked}
                className={`
                  rounded-2xl p-5 shadow-soft animate-fadeIn
                  border-2 transition-all duration-200
                  ${isLocked 
                    ? 'bg-gray-50 border-dashed border-gray-200 cursor-pointer' 
                    : 'bg-white border-transparent hover:border-[#4FB3A5]/40 cursor-pointer card-hover group'
                  }
                `}
                style={{ animationDelay: `${(index + 2) * 0.1}s`, opacity: 0 }}
                onClick={() => handleSelectRelationship(type)}
              >
                <div className="flex items-center gap-4">
                  <div className={`
                    w-12 h-12 rounded-xl flex items-center justify-center transition-colors duration-300
                    ${isLocked 
                      ? 'bg-gray-100' 
                      : 'bg-[#EAF6F4] group-hover:bg-[#4FB3A5]'
                    }
                  `}>
                    <Icon 
                      className={`transition-colors duration-300 ${isLocked ? 'text-gray-400' : 'text-[#4FB3A5] group-hover:text-white'}`} 
                      size={24} 
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className={`font-semibold transition-colors ${isLocked ? 'text-gray-400' : 'text-[#2C2C2C] group-hover:text-[#1F3A5F]'}`}>
                        {type.label}
                      </h3>
                      {isLocked && (
                        <span className="text-xs font-normal bg-gray-200 text-gray-500 px-2 py-0.5 rounded-full">
                          Premium
                        </span>
                      )}
                    </div>
                    <p className={`text-sm ${isLocked ? 'text-gray-400' : 'text-[#6B7280]'}`}>{type.description}</p>
                  </div>
                  {isLocked ? (
                    <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                      <Lock className="text-gray-500" size={14} />
                    </div>
                  ) : (
                    <ArrowRight className="text-[#A0A7B4] group-hover:text-[#4FB3A5] transition-colors" size={20} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Trust Message */}
      <div className="mt-12 text-center max-w-md animate-fadeIn stagger-4">
        <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 shadow-soft mb-4">
          <div className="w-2 h-2 rounded-full bg-[#7BC4A4]"></div>
          <span className="text-sm text-[#6B7280]">Your conversations stay private</span>
        </div>
        <p className="text-sm text-[#6B7280]">
          Designed to support healthier communication.
        </p>
      </div>

      {/* Footer */}
      <div className="mt-8 text-xs text-[#A0A7B4]">
        SayItBetter.me
      </div>
    </div>
  );
}
